import React, { useState, useEffect } from 'react';
import { detectEmailStyle } from '../utils/styleDetection';
import { STYLE_DESCRIPTIONS } from '../types';
import { Sparkles, Lightbulb, Clock, Zap, Mic } from 'lucide-react';

// Debounce hook to prevent excessive re-renders
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const StoryInput = ({
  story,
  onStoryChange,
  selectedStyle,
  onStyleChange,
  className = ''
}) => {
  const [suggestedStyle, setSuggestedStyle] = useState('');

  // Debounce story changes for style detection (500ms)
  const debouncedStory = useDebounce(story, 500);

  const exampleStories = [
    {
      text: "Yesterday I spent 3 hours fixing a tiny bug in my code. Then I realized I had a typo in one line.",
      style: "Oh No You Didn't",
      icon: Clock
    },
    {
      text: "I discovered a 5-minute daily habit that tripled my productivity and I haven't told anyone about it until now.",
      style: "Secret Shortcut", 
      icon: Zap
    },
    {
      text: "My client said my website looked 'amateur' and it stung. But she was right, and here's how I fixed it in one weekend.",
      style: "Show, Don't Tell",
      icon: Lightbulb
    }
  ];

  // Auto-detect style when debounced story changes (suggestion only, no auto-selection)
  useEffect(() => {
    if (debouncedStory.trim()) {
      const detected = detectEmailStyle(debouncedStory);
      setSuggestedStyle(detected);
    } else {
      setSuggestedStyle('');
    }
  }, [debouncedStory]);

  return (
    <div className={`space-y-4 ${className}`}>
      <div>
        <label htmlFor="story" className="block text-sm font-medium text-gray-700 mb-2">
          Your Story
        </label>
        <textarea
          id="story"
          value={story}
          onChange={(e) => onStoryChange(e.target.value)}
          placeholder="Paste your 1–3 sentence story, insight, or moment..."
          className="textarea-field min-h-[120px]"
          rows={4}
        />
        <div className="mt-2 flex items-start space-x-2">
          <Mic className="w-4 h-4 text-primary-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-gray-600">
            <span className="font-medium text-primary-600">Pro tip:</span> Try using your phone's microphone button to talk through your story. Speaking often captures more detail and emotion than typing.
          </p>
        </div>
      </div>

      {/* Example Stories */}
      {!story.trim() && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Sparkles className="w-5 h-5 text-blue-600" />
            <h3 className="text-sm font-semibold text-blue-900">Try an example story</h3>
          </div>
          <div className="space-y-3">
            {exampleStories.map((example, index) => {
              const IconComponent = example.icon;
              return (
                <div
                  key={index}
                  onClick={() => onStoryChange(example.text)}
                  className="p-4 bg-white/80 rounded-lg border border-blue-200 cursor-pointer hover:bg-white hover:shadow-md transition-all duration-200 group"
                >
                  <div className="flex items-start space-x-3">
                    <IconComponent className="w-4 h-4 text-blue-600 mt-1 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-700 group-hover:text-gray-900 leading-relaxed">
                        "{example.text}"
                      </p>
                      <div className="mt-2 flex items-center space-x-2">
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                          {example.style}
                        </span>
                        <span className="text-xs text-blue-600 group-hover:text-blue-700">
                          Click to try →
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Style Suggestion */}
      {suggestedStyle && story.trim() && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Lightbulb className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1">
                <span className="text-sm font-medium text-primary-900">
                  Suggested Style:
                </span>
                <span className={`text-sm font-semibold ${
                  selectedStyle === suggestedStyle 
                    ? 'text-green-700' 
                    : 'text-primary-700'
                }`}>
                  {suggestedStyle}
                </span>
                <Sparkles className="w-4 h-4 text-primary-500" />
                {selectedStyle === suggestedStyle && (
                  <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                    Selected
                  </span>
                )}
              </div>
              <p className="text-sm text-primary-700">
                {STYLE_DESCRIPTIONS[suggestedStyle]}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoryInput; 